import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  EditableComponent,
  ModelProps
} from '@adobe/aem-react-editable-components'
import {
  Container as UIFContainer,
  Tabs as UIFoundationsTabs,
  TabsVariants,
  TabsThemes,
  TabsHorizontalAlignments,
  TabsScrollButtonsProps
} from '@dcxwam/dcx-wam-ui-foundations'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import Container from '../../templates/Container/Container'
import TabsEditConfig from './TabsEditConfig'
import { TabsModelProps, CqItemModel } from './types'
import { EditableComponentPropsType } from '../../../utils/prop-types'
import { useIsSsr } from '../../../utils/isSsr'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'
import {
  WithAutomaticTestIdsType,
  withAutomaticTestIds
} from '../../../utils/aem/with-automatic-test-ids'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'

const ALIGNMENT_MAP: Record<
  NonNullable<TabsModelProps['tabsAlignment']>,
  TabsHorizontalAlignments
> = {
  start: TabsHorizontalAlignments.Start,
  center: TabsHorizontalAlignments.Center,
  end: TabsHorizontalAlignments.End
}

const THEME_MAP: Record<'dark' | 'light', TabsThemes> = {
  dark: TabsThemes.Dark,
  light: TabsThemes.Light
}

const Tabs = withComponentRefresh(
  withAutomaticTestIds(
    (
      props: WithAutomaticTestIdsType<
        EditableComponentPropsType<TabsModelProps>
      >
    ) => {
      const {
        cqItems,
        cqItemsOrder,
        id,
        ariaLabel,
        tabsAlignment = 'start',
        tabsWidth = 'boxed',
        theme = 'unset',
        ...rest
      } = props.model
      const { getDataTestIdObject } = props
      const editableProps = useExtractEditableProps(props, rest)
      const isSsr = useIsSsr()

      const wrapperRef = useRef<HTMLDivElement>(null)
      const [value, setValue] = useState(0)
      const scrollNeededRef = useRef(false)

      const handleChange = useCallback(
        (_event: SyntheticEvent, newValue: number) => {
          setValue(newValue)
        },
        []
      )

      const tabsArray = useMemo<
        Array<{ key: string; item: CqItemModel }>
      >(() => {
        const entries = Object.entries(cqItems ?? {}) as Array<
          [string, CqItemModel]
        >
        const orderedEntries = cqItemsOrder
          ? cqItemsOrder
              .filter((key) => !!cqItems?.[key])
              .map((key) => [key, cqItems?.[key] as CqItemModel] as const)
          : entries
        return orderedEntries.map(([key, item]) => ({ key, item }))
      }, [cqItems, cqItemsOrder])

      useEffect(() => {
        if (isSsr || typeof window === 'undefined') {
          return
        }
        const hash = window.location.hash.replace(/^#/, '')
        if (!hash) {
          return
        }
        const matchedIndex = tabsArray.findIndex(
          ({ item }) => item?.id === hash
        )
        if (matchedIndex >= 0) {
          scrollNeededRef.current = true
          setValue(matchedIndex)
        }
      }, [isSsr, tabsArray])

      useEffect(() => {
        if (!scrollNeededRef.current) {
          return
        }
        scrollNeededRef.current = false
        wrapperRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, [value])

      const tabLabels = tabsArray.map(({ item }) => {
        const hasImage = !!(item?.imagePath || item?.imageSrcSet)

        let srcSetData: string | undefined
        if (item?.imageSrcSet) {
          if (Array.isArray(item.imageSrcSet)) {
            srcSetData = item.imageSrcSet.join(', ')
          } else if (typeof item.imageSrcSet === 'string') {
            srcSetData = item.imageSrcSet
          }
        }

        return {
          textProps: { content: item?.title || '' },
          ...(item?.icon && { icon: item.icon }),
          ...(hasImage && {
            imageProps: {
              src: item?.imagePath,
              height: item?.imageHeight,
              width: item?.imageWidth,
              alt: item?.imgAlt,
              srcSetData
            }
          }),
          ...(item?.id && { id: item.id }),
          ...(item?.ariaLabel && { 'aria-label': item.ariaLabel })
        }
      })

      const tabContents = tabsArray.map(({ item }, index) => {
        const tabContent = {
          model: {
            ...item,
            cqItems: item[':items'],
            cqItemsOrder: item[':itemsOrder'],
            cqType: item[':type']
          }
        }
        return {
          id: item?.id || `${id || 'tabs'}-panel-${index}`,
          content: <Container model={{ ...tabContent } as ModelProps} />,
          index
        }
      })

      const tabsTheme = theme === 'unset' ? undefined : THEME_MAP[theme]

      const tabsConfig = {
        tabLabels,
        tabContents,
        value,
        onChange: handleChange,
        tabsVariant:
          tabsWidth === 'full'
            ? TabsVariants.FullWidth
            : TabsVariants.Scrollable,
        horizontalAlignment: ALIGNMENT_MAP[tabsAlignment],
        ...(tabsTheme && { tabsTheme }),
        displaySelector: true,
        scrollButtons: TabsScrollButtonsProps.Auto,
        'aria-label': ariaLabel,
        id,
        ...getDataTestIdObject('tabs', id)
      }

      return (
        <EditableComponent {...editableProps}>
          {TabsEditConfig.isEmpty(props) ? TabsEditConfig.emptyLabel : null}
          {!TabsEditConfig.isEmpty(props) &&
            (AuthoringUtils.isInEditor() ? (
              <Container {...props} />
            ) : (
              <UIFContainer innerRef={wrapperRef}>
                <UIFoundationsTabs {...tabsConfig} />
              </UIFContainer>
            ))}
        </EditableComponent>
      )
    }
  )
)

const TabsWithErrorBoundary = withErrorBoundary(Tabs, {
  componentName: 'Tabs',
  fallback: <ErrorFallback componentName="Tabs" />
})

export default TabsWithErrorBoundary
